import { Dialog, DialogContent } from '@mui/material';
import type { PatientsType } from '@/components/Patient/components/util/common/type';
import { useContext, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { emptyPatients } from '../../../common/emptyData';
// import { useOptionButtons } from '@/feature/phr-29/components/Patients/utils/searchFunction';
import { ScrollArea } from '@mantine/core';
import {
  dateFromUnixTime,
  // japanTimeFormatDay関数は使用しないためコメントアウト
  // japanTimeFormatDay,
} from '../../mastaRegistration/shiftRegistration/DailyCalendar/CalendarCommon';
import { ClinicContext, useGraphqlClient } from '@/App';
import { OrderAndFilterControllerForModal } from '@/components/Patient/components/views/OrderAndFilterController';
import { usePatientsPagination } from '@/components/Patient/components/views/hooks/usePatientsPagination';
// import { is } from 'date-fns/locale';
import { Pagination } from '@/components/Patient/components/views/Pagination';

interface SearchPatientType {
  setOpenSearchPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAddPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
}

function createHandleClose(
  setOpenSearchPanel: React.Dispatch<React.SetStateAction<boolean>>,
  setResult: React.Dispatch<React.SetStateAction<PatientsType[]>>
) {
  return function handleClose() {
    setResult([emptyPatients]);
    setOpenSearchPanel(false);
  };
}

const ITEMS_PER_PAGE = 50;

// Header Component
const Header: React.FC<any> = ({
  patients,
  setOpenSearchPatientModal,
  setOpenAddPatientModal,
  orderAndFilterController,
  setIsSearched,
}) => {
  return (
    <header className="w-full top-0 left-0 pt-8 bg-white bg-opacity-90 border-b border-gray-600 pb-2 mb-2">
      <p className="text-xl spacing-ls tracking-wider border-b border-gray-600 pb-2 mb-10">
        <span className="text-red-500">患者カード</span>を選択してください。
      </p>
      <div className="mt-8 flex justify-center">
        <OrderAndFilterControllerForModal
          result={patients}
          setOpenSearchPanel={setOpenSearchPatientModal}
          setOpenAddPatientModal={setOpenAddPatientModal}
          orderAndFilterController={orderAndFilterController}
          setIsSearched={setIsSearched}
        />
      </div>
    </header>
  );
};

// 和暦変換用の関数を追加
function formatJapaneseDateFromUnixTime(unixTime: number | undefined | null): string {
  if (unixTime === undefined || unixTime === null) return '----年--月--日';
  
  const date = dateFromUnixTime(unixTime);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  let eraName = '';
  let eraYear = 0;
  
  if (year >= 2019) {
    // 令和: 2019年5月1日～
    eraName = '令和';
    eraYear = year - 2019 + 1;
  } else if (year >= 1989) {
    // 平成: 1989年1月8日～2019年4月30日
    eraName = '平成';
    eraYear = year - 1989 + 1;
  } else if (year >= 1926) {
    // 昭和: 1926年12月25日～1989年1月7日
    eraName = '昭和';
    eraYear = year - 1926 + 1;
  } else if (year >= 1912) {
    // 大正: 1912年7月30日～1926年12月24日
    eraName = '大正';
    eraYear = year - 1912 + 1;
  } else if (year >= 1868) {
    // 明治: 1868年1月25日～1912年7月29日
    eraName = '明治';
    eraYear = year - 1868 + 1;
  } else {
    // 明治以前はそのまま西暦表示
    return `${year}年${month}月${day}日`;
  }
  
  return `${eraName}${eraYear}年${month}月${day}日`;
}

// PatientLabel Component
const PatientLabel: React.FC<any> = ({
  getValues,
  patient,
  clinic,
  setOpenSearchPatientModal,
  isSearched,
  register,
  watch,
}) => {
  const patientNumber = getPatientNumber(patient, clinic.clinic_label);

  return (
    <label
      key={patient.id}
      className={`${
        getValues('appointData.id') === patient.id
          ? 'relative bg-gray-100 text-blue-500 font-bold'
          : 'bg-white'
      } justify-center tracking-wider text-xl relative flex items-center space-x-3 rounded border border-gray-300 bg-white px-6 py-6 shadow`}
    >
      <div className="flex w-full">
        <div className="w-1/3 text-right">{patientNumber}</div>
        <div className="w-1/3 text-center">
          {patient.name.last} {patient.name.first}
        </div>
        <div className="w-1/3 text-left">
          {formatJapaneseDateFromUnixTime(patient?.birthday)}
        </div>
      </div>
      <input
        onClick={() => {
          setOpenSearchPatientModal(false);
        }}
        {...(isSearched === true ? register('appointData.id') : { onChange: () => {} })}
        className={`${
          getValues('appointData.id') === patient.id ? 'opacity-100' : 'opacity-0'
        } checked:bg-none h-4 w-4 absolute top-0 left-0`}
        checked={isSearched === true ? watch('appointData.id') === patient.id : false}
        type="radio"
        value={patient.id}
      />
    </label>
  );
};

function getPatientNumber(patient: PatientsType, clinicLabel: string): string {
  let patientNumber = '';
  if (clinicLabel === 'eldear') {
    patientNumber = `S-${patient?.SNumber ?? '---'} : D-${patient?.DNumber ?? '---'}`;
  } else {
    patientNumber = `${patient?.SNumber ?? '---'}`;
  }
  return patientNumber;
}

// 患者リスト表示コンポーネント
const PatientList: React.FC<{
  filteredResults: PatientsType[];
  isSearched: boolean;
  getValues: any;
  setOpenSearchPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  register: any;
  watch: any;
}> = ({ filteredResults, isSearched, getValues, setOpenSearchPatientModal, register, watch }) => {
  return (
    <ScrollArea
      style={{ height: 210 }}
      type="scroll"
      scrollbarSize={6}
      scrollHideDelay={300}
    >
      <div className={`${!isSearched ? 'pointer-events-none bg-gray-100' : ''}`}>
        <div className={`${!isSearched ? 'opacity-40' : ''} grid grid-cols-1 gap-y-2`}>
          {filteredResults.map((patient) => (
            <PatientLabel
              key={patient.id}
              getValues={getValues}
              patient={patient}
              clinic={useContext(ClinicContext)}
              setOpenSearchPatientModal={setOpenSearchPatientModal}
              isSearched={isSearched}
              register={register}
              watch={watch}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
};

// ページネーションとフィルタリングを管理するコンポーネント
const PatientsFilteringAndPagination: React.FC<{
  patients: PatientsType[];
  orderAndFilterController: any;
  getValues: any;
  setOpenSearchPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAddPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSearched: React.Dispatch<React.SetStateAction<boolean>>;
  isSearched: boolean;
  register: any;
  watch: any;
}> = ({
  patients,
  orderAndFilterController,
  getValues,
  setOpenSearchPatientModal,
  setOpenAddPatientModal,
  setIsSearched,
  isSearched,
  register,
  watch,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const filteredResults = patients
    .filter((obj) => obj.status !== 'true')
    .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const {orderOption, filterOption} = orderAndFilterController;

  useEffect(()=>{
    // フィルター/ソートオプションが変更されたらページをリセット
    setCurrentPage(1);
  },[orderOption, filterOption]);

  return (
    <>
      <Header
        getValues={getValues}
        patients={patients}
        setOpenSearchPatientModal={setOpenSearchPatientModal}
        setOpenAddPatientModal={setOpenAddPatientModal}
        orderAndFilterController={orderAndFilterController}
        setIsSearched={setIsSearched}
      />
      <Pagination
        itemsPerPage={ITEMS_PER_PAGE}
        totalItems={patients.length}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
      />
      <PatientList
        filteredResults={filteredResults}
        isSearched={isSearched}
        getValues={getValues}
        setOpenSearchPatientModal={setOpenSearchPatientModal}
        register={register}
        watch={watch}
      />
    </>
  );
};

// ダイアログのコンテンツを別コンポーネントに分離
const SearchPatientDialogContent: React.FC<{
  patients: PatientsType[];
  setResult: React.Dispatch<React.SetStateAction<PatientsType[]>>;
  orderAndFilterController: any;
  isSearched: boolean;
  setIsSearched: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSearchPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAddPatientModal: React.Dispatch<React.SetStateAction<boolean>>;
  getValues: any;
  register: any;
  watch: any;
}> = ({
  patients,
  setResult,
  orderAndFilterController,
  isSearched,
  setIsSearched,
  setOpenSearchPatientModal,
  setOpenAddPatientModal,
  getValues,
  register,
  watch,
}) => {
  return (
    <div className="wrapper h-full overflow-y-hidden">
      <div className="pb-8 px-8">
        <PatientsFilteringAndPagination
          patients={patients}
          orderAndFilterController={orderAndFilterController}
          getValues={getValues}
          setOpenSearchPatientModal={setOpenSearchPatientModal}
          setOpenAddPatientModal={setOpenAddPatientModal}
          setIsSearched={setIsSearched}
          isSearched={isSearched}
          register={register}
          watch={watch}
        />
      </div>
    </div>
  );
};

// ダイアログのスタイル設定
const dialogPaperProps = {
  style: {
    maxWidth: 'none',
    width: '66%',
    transition: 'all 0',
    zIndex: 9999,
  },
};

// ダイアログコンテンツのスタイル設定
const dialogContentStyle = {
  padding: '0px 0px 0px 0px',
  maxWidth: 'none',
  maxHeight: 'none',
  height: '70vh',
  position: 'relative' as const,
};

export const SearchPatientModal: React.FC<SearchPatientType> = ({
  setOpenSearchPatientModal,
  setOpenAddPatientModal,
}) => {
  const { register, getValues, watch } = useFormContext();
  const [isSearched, setIsSearched] = useState(false);
  const graphqlClient = useGraphqlClient();
  const clinicId = useContext(ClinicContext).clinic_id;
  const { patients, setResult, orderAndFilterController } = usePatientsPagination(
    graphqlClient,
    clinicId
  );

  return (
    <Dialog
      transitionDuration={{ appear: 0, enter: 0, exit: 0 }}
      onClose={createHandleClose(setOpenSearchPatientModal, setResult)}
      open={true}
      PaperProps={dialogPaperProps}
    >
      <DialogContent dividers style={dialogContentStyle}>
        <SearchPatientDialogContent
          patients={patients}
          setResult={setResult}
          orderAndFilterController={orderAndFilterController}
          isSearched={isSearched}
          setIsSearched={setIsSearched}
          setOpenSearchPatientModal={setOpenSearchPatientModal}
          setOpenAddPatientModal={setOpenAddPatientModal}
          getValues={getValues}
          register={register}
          watch={watch}
        />
      </DialogContent>
    </Dialog>
  );
};
